import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  EMAIL_ATTACHMENT_QUERY_KEY,
  INVOICE_DETAIL_QUERY_KEY,
  INVOICE_HISTORY_QUERY_KEY,
  INVOICE_QUEUE_LIST_QUERY_KEY,
  PDF_FILE_QUERY_KEY,
  QUEUE_QUERY_KEY,
  QUEUE_STATISTICS_QUERY_KEY,
  INVOICE_GET_IDS_QUERY_KEY,
  QUEUE_DROPDOWN_KEY
} from "../constants"
import {
  checkoutInvoice,
  getEmailAttachments,
  getInvoiceQueue,
  getInvoiceStatistics,
  getPDFFromGCP,
  invoiceHistory,
  invoiceQueues,
  moveInvoices,
  rejectInvoice,
  saveInvoice,
  SaveInvoiceArgs,
  unassignInvoices,
  getInvoiceByIds,
  getInvoiceFiltersDrpDowns
} from "../service/InvoiceQueue"
import {
  InvoiceDetailRead,
  InvoiceRejectionCreate,
  InvoicesAllRead,
  StatusGroup
} from "../api/generated"
import { InvoiceQueueFilters } from "../components/InvoicesTable/InvoicesTable";

export const useInvoiceQueue = (  statusGroup: StatusGroup,filters: InvoiceQueueFilters = {}) =>
  useQuery({
      queryKey: [QUEUE_QUERY_KEY, statusGroup, filters],
      queryFn: () => getInvoiceQueue(statusGroup, filters),
  })

export const useInvoiceStatistics = (queueId: number,filters) =>
  useQuery({
    queryKey: [QUEUE_STATISTICS_QUERY_KEY, queueId,filters],
    queryFn: () => {
      return getInvoiceStatistics(queueId === -1 ? undefined : queueId, filters)
    },
    placeholderData: {
      invoice_count: "",
      today: "",
      oneToSevenDays: "",
      eightToFifteenDays: "",
      sixteenPlusDays: "",
    } as any,
  })

export const useInvoiceDetails = (invoiceId: number) =>
  useQuery({
    queryKey: [INVOICE_DETAIL_QUERY_KEY, invoiceId],
    queryFn: () => checkoutInvoice(invoiceId),
  })

export const useInvoiceIds = (invoiceIds: number[]) =>
  useQuery({
    queryKey: [INVOICE_GET_IDS_QUERY_KEY, invoiceIds],
    queryFn: () => getInvoiceByIds(invoiceIds),
    enabled: false
  })

export const useInvoiceUnassign = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceIds: number[]) => unassignInvoices(invoiceIds),
    onSuccess: (res) => {
      const ids = res.map((elem) => elem.id)
      queryClient.invalidateQueries({queryKey: [QUEUE_QUERY_KEY]});
      queryClient.setQueryData(
        [QUEUE_QUERY_KEY],
        (old: InvoiceDetailRead[]) => {
          if (!old) {
            return null
          }
          return old.map((record) => {
            if (ids.includes(record.id)) {
              return { ...record, assigned_user_email: null }
            }
            return record
          })
        },
      )
      queryClient.invalidateQueries({
        queryKey: [[QUEUE_STATISTICS_QUERY_KEY]],
      })
    },
  })
}

export const useInvoiceSkipValidation = () => {
  return useMutation({
    mutationFn: (saveInvoiceArgs: SaveInvoiceArgs) =>
      saveInvoice(saveInvoiceArgs),
  })
}

export const useInvoiceSave = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (saveInvoiceArgs: SaveInvoiceArgs) =>
      saveInvoice(saveInvoiceArgs),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: [INVOICE_DETAIL_QUERY_KEY, res.id],
      })
    },
  })
}

export interface MoveInvoiceArgs {
  invoiceIds: number[]
  queueId: number
}

export const useRejectInvoice = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rejectionDetails: RejectInvoiceArgs) =>
      rejectInvoice(
        rejectionDetails.invoiceIds,
        rejectionDetails.rejectionDetails
      ),
    onSuccess: (res) => {
      const ids = res.map((elem) => elem.id)
      queryClient.invalidateQueries({queryKey: [QUEUE_QUERY_KEY]});
      queryClient.invalidateQueries({queryKey: [QUEUE_STATISTICS_QUERY_KEY]});
      queryClient.setQueryData([QUEUE_QUERY_KEY], (old: InvoicesAllRead[]) => {
        if (!old) {
          return null
        }

        const newRecords = old.filter((record) => !ids.includes(record.id))
        return newRecords
      })
    },
  })
}

export const useMoveInvoices = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (moveInvoiceArgs: MoveInvoiceArgs) =>
      moveInvoices(moveInvoiceArgs.invoiceIds, moveInvoiceArgs.queueId),
    onSuccess: (res) => {
      const ids = res.map((elem) => elem.id)
      queryClient.invalidateQueries({queryKey: [QUEUE_QUERY_KEY]});
      const queueId = res[0].queue_id
      queryClient.setQueryData([QUEUE_QUERY_KEY], (old: InvoicesAllRead[]) => {
        if (!old) {
          return null
        }
        return old.map((record) => {
          if (ids.includes(record.id)) {
            return { ...record, queue_id: queueId }
          }
          return record
        })
      })
    },
  })
}
export const useEmailAttachments = (emailId: number) =>
  useQuery({
    queryKey: [EMAIL_ATTACHMENT_QUERY_KEY],
    queryFn: () => getEmailAttachments(emailId),
  })

export const useInvoiceHistory = (invoiceId: number) =>
  useQuery({
    queryKey: [INVOICE_HISTORY_QUERY_KEY, invoiceId],
    queryFn: () => invoiceHistory(invoiceId),
  })

export const useInvoiceQueues = (currentUserOnly: boolean) =>
  useQuery({
    queryKey: [INVOICE_QUEUE_LIST_QUERY_KEY, currentUserOnly],
    queryFn: () => invoiceQueues(currentUserOnly),
  })

export interface RejectInvoiceArgs {
  invoiceIds: number[]
  rejectionDetails: InvoiceRejectionCreate
}

export const useGetInvoicefilterDrpdown = (statusGroup: StatusGroup) => {
  return useQuery({
    queryKey: [QUEUE_DROPDOWN_KEY, statusGroup],
    queryFn: () => getInvoiceFiltersDrpDowns(statusGroup)
  });
}
